/**
 * Lift rules on the calendar: what the Block time dialog and the Site row's panel
 * are handed, so that those draw it and fetch nothing.
 *
 * Writes answer rather than throw: a refusal stays in the dialog that asked, and
 * only the last-way-home refusal needs a second question.
 */
import { useCallback, useMemo } from 'react';
import { apiErrorCode } from '../../../api/hooks/useFireMode';
import {
  cutOffLevelsOf,
  LIFT_RULES_NOT_REPORTING,
  liftRulesOn,
  useCreateLiftRuleWindow,
  useDeleteLiftRuleWindow,
  useLiftRules,
  useLiftRuleWindows,
  useSkipLiftRuleOccurrence,
} from '../../../api/hooks/useLiftRules';
import type { CreateLiftRuleWindowRequest, LiftRuleWindow } from '../../../api/v1';
import { calendarLiftChoices } from '../../Devices/lift-rules-words';

export type LiftWindowCreated =
  | { readonly ok: true }
  | { readonly ok: false; readonly cutOff: readonly string[] }
  | { readonly ok: false; readonly cutOff?: undefined; readonly words: string };

export interface LiftCalendar {
  /** Lift rules are on at this site. Off, or not known yet: the calendar is as
   *  it was before them. */
  readonly on: boolean;
  /** Which lifts each kind of run can name; null while the fleet is not
   *  reporting, when none can be set. */
  readonly choices: { block_use: string[]; allow_travel: string[] } | null;
  /** Lift names the same rule words use when the rules are not known. */
  readonly state: ReturnType<typeof useLiftRules>['state'];
  readonly create: (body: CreateLiftRuleWindowRequest) => Promise<LiftWindowCreated>;
  readonly remove: (windowId: string) => Promise<string | null>;
  readonly skip: (windowId: string, startMs: number) => Promise<string | null>;
  readonly windowOf: (windowId: string) => LiftRuleWindow | null;
}

function words(err: unknown): string {
  if (apiErrorCode(err) === 'lift_rules_off') {
    return 'Lift rules are switched off for this site, so nothing was changed.';
  }
  if (apiErrorCode(err) === LIFT_RULES_NOT_REPORTING) {
    return 'The robots’ system is not reporting its lifts just now, so nothing was changed. Try again shortly.';
  }
  return err instanceof Error && err.message ? err.message : 'That did not work. Nothing was changed.';
}

/** `null` from the two below is success; a string is why not. */
export function useLiftCalendar(): LiftCalendar {
  const rules = useLiftRules();
  const on = liftRulesOn(rules.state);
  const windows = useLiftRuleWindows(on).data;
  const create = useCreateLiftRuleWindow();
  const remove = useDeleteLiftRuleWindow();
  const skip = useSkipLiftRuleOccurrence();
  const createAsync = create.mutateAsync;
  const removeAsync = remove.mutateAsync;
  const skipAsync = skip.mutateAsync;
  const view = rules.view;
  const choices = useMemo(() => (view ? calendarLiftChoices(view) : null), [view]);
  return {
    on,
    choices,
    state: rules.state,
    create: useCallback(
      (body) =>
        createAsync(body).then(
          (): LiftWindowCreated => ({ ok: true }),
          (err: unknown): LiftWindowCreated => {
            const levels = cutOffLevelsOf(err);
            return levels ? { ok: false, cutOff: levels } : { ok: false, words: words(err) };
          },
        ),
      [createAsync],
    ),
    remove: useCallback(
      (windowId) => removeAsync(windowId).then(() => null, words),
      [removeAsync],
    ),
    skip: useCallback(
      (windowId, startMs) => skipAsync({ windowId, startMs }).then(() => null, words),
      [skipAsync],
    ),
    windowOf: useCallback(
      (windowId) => windows?.find((w) => w.window_id === windowId) ?? null,
      [windows],
    ),
  };
}
